'use client';
import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const testimonials = [
  {
    quote: 'Perdi R$ 47 mil em 8 meses. Minha esposa quase foi embora. Hoje estou há 180 dias sem apostar — e minha família está de volta.',
    name: 'Rafael S.',
    city: 'São Paulo',
    days: '180 dias limpo',
    color: '#7C9E87',
  },
  {
    quote: 'Eu escondia o celular da minha mãe pra apostar de madrugada. Achei que era só diversão. Quando percebi, tinha dívidas que não consigo nem falar o valor.',
    name: 'Thiago M.',
    city: 'Belo Horizonte',
    days: '94 dias limpo',
    color: '#C4A882',
  },
  {
    quote: 'Tentei parar sozinha três vezes. Não consegui. Aqui encontrei pessoas que passaram pelo mesmo — e isso mudou tudo.',
    name: 'Camila R.',
    city: 'Porto Alegre',
    days: '61 dias limpa',
    color: '#7C9E87',
  },
];

const features = [
  {
    icon: '🛡️',
    title: 'Modo Crise',
    desc: 'Quando a vontade de apostar bater forte, você tem um botão de emergência. Técnicas imediatas para segurar o impulso.',
    span: 'col-span-2',
  },
  {
    icon: '💬',
    title: 'Chat ao Vivo',
    desc: 'Voluntários reais, disponíveis agora.',
    span: 'col-span-1',
  },
  {
    icon: '📅',
    title: 'Streak Diário',
    desc: 'Cada dia sem apostar conta. Veja seu progresso crescer.',
    span: 'col-span-1',
  },
  {
    icon: '💰',
    title: 'Dinheiro Recuperado',
    desc: 'Veja em tempo real quanto você está economizando desde que parou. O número que vai te motivar a continuar.',
    span: 'col-span-2',
  },
  {
    icon: '👥',
    title: 'Comunidade',
    desc: 'Grupos de apoio com quem entende.',
    span: 'col-span-1',
  },
];

const warningSignals = [
  'Você aposta mais do que planejou',
  'Mente para família sobre quanto perdeu',
  'Pensa em apostas o tempo todo',
  'Apostou dinheiro que não tinha',
  'Tentou parar e não conseguiu',
  'Sente euforia só ao apostar',
];

export default function LandingPage() {
  const [checkedSignals, setCheckedSignals] = useState<number[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSignal = (i: number) => {
    setCheckedSignals(prev =>
      prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
    );
    setShowResult(false);
  };

  const riskLevel = checkedSignals.length;

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: '#1A1A2E', color: '#F8F7F4' }}>

      {/* ── NAV ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4 transition-all duration-300"
        style={{
          backgroundColor: scrollY > 40 ? 'rgba(26,26,46,0.95)' : 'transparent',
          backdropFilter: scrollY > 40 ? 'blur(12px)' : 'none',
          borderBottom: scrollY > 40 ? '1px solid rgba(196,168,130,0.15)' : 'none',
        }}
      >
        <Image
          src="/assets/images/logo_1dia_transparente-1777268210110.png"
          alt="1 Dia de Cada Vez"
          width={120}
          height={40}
          className="object-contain"
          style={{ maxHeight: '38px' }}
        />
        <div className="flex items-center gap-4">
          <Link
            href="/quem-somos"
            className="text-sm font-medium transition-colors hover:opacity-80 hidden sm:inline"
            style={{ color: 'rgba(248,247,244,0.75)' }}
          >
            Quem Somos
          </Link>
          <Link
            href="/apoie-a-pagina"
            className="text-sm font-medium transition-colors hover:opacity-80 hidden sm:inline"
            style={{ color: '#C4A882' }}
          >
            💛 Apoie a Página
          </Link>
          <Link
            href="/sign-up-login"
            className="px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 hover:scale-105"
            style={{ backgroundColor: '#7C9E87', color: '#fff' }}
          >
            Quero Sair Disso
          </Link>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-24 pb-16 overflow-hidden"
      >
        {/* Animated background blobs */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <div
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl"
            style={{ backgroundColor: '#7C9E87', animation: 'pulseSoft 6s ease-in-out infinite' }}
          />
          <div
            className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full opacity-15 blur-3xl"
            style={{ backgroundColor: '#C4A882', animation: 'pulseSoft 8s ease-in-out infinite 2s' }}
          />
          <div
            className="absolute top-1/2 left-1/2 w-64 h-64 rounded-full opacity-10 blur-3xl"
            style={{ backgroundColor: '#252550', animation: 'pulseSoft 10s ease-in-out infinite 4s' }}
          />
        </div>

        {/* Grain overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-30"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")',
          }}
        />

        <div className="relative z-10 max-w-3xl mx-auto text-center space-y-8">
          {/* Urgency badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest"
            style={{ backgroundColor: 'rgba(196,168,130,0.2)', border: '1px solid rgba(196,168,130,0.4)', color: '#C4A882' }}
          >
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#C4A882' }} />
            Você não está sozinho nisso
          </div>

          <h1
            className="font-serif leading-tight"
            style={{ fontSize: 'clamp(2.4rem, 6vw, 4rem)', color: '#F8F7F4' }}
          >
            Você sabe que{' '}
            <span style={{ color: '#7C9E87' }}>o jogo está te destruindo.</span>
            <br />
            Mas não consegue parar.
          </h1>

          <p
            className="text-lg leading-relaxed max-w-xl mx-auto"
            style={{ color: 'rgba(248,247,244,0.75)' }}
          >
            Não é fraqueza. É vício. E vício tem saída — quando você não precisa enfrentar sozinho.
            O <strong style={{ color: '#F8F7F4' }}>1 Dia de Cada Vez</strong> é o lugar onde pessoas reais te ajudam a dar o primeiro passo.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/sign-up-login"
              className="group px-8 py-4 rounded-2xl text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center justify-center gap-2"
              style={{
                backgroundColor: '#7C9E87',
                color: '#fff',
                boxShadow: '0 0 40px rgba(124,158,135,0.4)',
              }}
            >
              Quero Começar Agora
              <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
            </Link>
            <a
              href="#quiz"
              className="px-8 py-4 rounded-2xl text-base font-semibold transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2"
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: '#F8F7F4',
              }}
            >
              Fazer o teste rápido
            </a>
          </div>

          <p className="text-xs" style={{ color: 'rgba(248,247,244,0.4)' }}>
            100% gratuito · Confidencial · Sem julgamentos
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
          <span className="text-xs" style={{ color: 'rgba(248,247,244,0.5)' }}>role para baixo</span>
          <div className="w-px h-8 animate-pulse" style={{ backgroundColor: '#C4A882' }} />
        </div>
      </section>

      {/* ── PAIN SECTION ── */}
      <section className="py-20 px-6" style={{ backgroundColor: '#111120' }}>
        <div className="max-w-4xl mx-auto">
          <div
            className="rounded-3xl p-8 md:p-12 space-y-6"
            style={{
              background: 'linear-gradient(135deg, rgba(37,37,80,0.8) 0%, rgba(26,26,46,0.9) 100%)',
              border: '1px solid rgba(196,168,130,0.2)',
            }}
          >
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#C4A882' }}>
              Isso soa familiar?
            </p>
            <h2
              className="font-serif leading-snug"
              style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', color: '#F8F7F4' }}
            >
              "Só mais uma aposta e eu recupero tudo."
            </h2>
            <div className="space-y-4 text-base leading-relaxed" style={{ color: 'rgba(248,247,244,0.75)' }}>
              <p>
                Você já prometeu para si mesmo que ia parar. Já prometeu para quem você ama. Mas quando a notificação chega, quando o jogo abre, algo dentro de você muda — e você aposta de novo.
              </p>
              <p>
                Não é falta de força de vontade. O vício em apostas <strong style={{ color: '#F8F7F4' }}>reprograma o cérebro</strong> da mesma forma que outras dependências. A dopamina do "quase ganhou" é mais viciante do que ganhar de verdade.
              </p>
              <p style={{ color: '#C4A882', fontWeight: 600 }}>
                Você não escolheu ficar preso nisso. Mas você pode escolher sair.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── QUIZ / SELF-CHECK ── */}
      <section id="quiz" className="py-20 px-6">
        <div className="max-w-2xl mx-auto space-y-8">
          <div className="text-center space-y-3">
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#7C9E87' }}>
              Teste rápido — 30 segundos
            </p>
            <h2
              className="font-serif"
              style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', color: '#F8F7F4' }}
            >
              Marque o que se aplica a você
            </h2>
            <p className="text-sm" style={{ color: 'rgba(248,247,244,0.55)' }}>
              Ninguém vai ver suas respostas. É só para você.
            </p>
          </div>

          <div className="space-y-3">
            {warningSignals.map((signal, i) => (
              <button
                key={i}
                onClick={() => toggleSignal(i)}
                className="w-full text-left px-5 py-4 rounded-2xl flex items-center gap-4 transition-all duration-200 hover:scale-[1.01]"
                style={{
                  backgroundColor: checkedSignals.includes(i)
                    ? 'rgba(124,158,135,0.2)'
                    : 'rgba(255,255,255,0.05)',
                  border: checkedSignals.includes(i)
                    ? '1px solid rgba(124,158,135,0.5)'
                    : '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200"
                  style={{
                    backgroundColor: checkedSignals.includes(i) ? '#7C9E87' : 'rgba(255,255,255,0.1)',
                    border: checkedSignals.includes(i) ? 'none' : '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  {checkedSignals.includes(i) && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <span className="text-sm font-medium" style={{ color: '#F8F7F4' }}>{signal}</span>
              </button>
            ))}
          </div>

          {checkedSignals.length > 0 && (
            <div
              className="rounded-2xl p-6 space-y-4 animate-fade-in"
              style={{
                backgroundColor: riskLevel >= 4
                  ? 'rgba(239,68,68,0.1)'
                  : riskLevel >= 2
                  ? 'rgba(196,168,130,0.15)'
                  : 'rgba(124,158,135,0.1)',
                border: `1px solid ${riskLevel >= 4 ? 'rgba(239,68,68,0.3)' : riskLevel >= 2 ? 'rgba(196,168,130,0.3)' : 'rgba(124,158,135,0.3)'}`,
              }}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">
                  {riskLevel >= 4 ? '🚨' : riskLevel >= 2 ? '⚠️' : '💛'}
                </span>
                <div>
                  <p
                    className="font-bold text-base"
                    style={{
                      color: riskLevel >= 4 ? '#ef4444' : riskLevel >= 2 ? '#C4A882' : '#7C9E87',
                    }}
                  >
                    {riskLevel >= 4
                      ? 'Sinal de alerta alto — você precisa de apoio agora'
                      : riskLevel >= 2
                      ? 'Sinais preocupantes — vale conversar com alguém' :'Fique atento — o vício começa assim'}
                  </p>
                  <p className="text-sm mt-1" style={{ color: 'rgba(248,247,244,0.65)' }}>
                    {riskLevel >= 4
                      ? 'Você marcou ' + riskLevel + ' sinais. Isso não é coincidência. Há pessoas prontas para te ouvir agora, sem julgamento.'
                      : riskLevel >= 2
                      ? 'Você marcou ' + riskLevel + ' sinais. Quanto mais cedo você busca apoio, mais fácil é sair.'
                      : 'Você marcou ' + riskLevel + ' sinal. Prevenção é o melhor caminho.'}
                  </p>
                </div>
              </div>
              <Link
                href="/sign-up-login"
                className="block w-full text-center py-3 rounded-xl font-bold text-sm transition-all duration-200 hover:scale-[1.02]"
                style={{
                  backgroundColor: riskLevel >= 4 ? '#ef4444' : '#7C9E87',
                  color: '#fff',
                }}
              >
                {riskLevel >= 4 ? 'Preciso de Ajuda Agora' : 'Criar Minha Conta Gratuita'}
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ── PILLARS ── */}
      <section className="py-16 px-6" style={{ backgroundColor: '#111120' }}>
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: '🔒', title: 'Confidencial', desc: 'Seus dados são seus. Ninguém sabe que você está aqui.' },
              { icon: '💚', title: '100% Gratuito', desc: 'Sem planos pagos, sem cobranças. Para sempre.' },
              { icon: '🤝', title: 'Sem Julgamentos', desc: 'Uma comunidade que entende porque já esteve lá.' },
              { icon: '⚡', title: 'Apoio Imediato', desc: 'Chat ao vivo com voluntários quando você mais precisa.' },
            ].map((p, i) => (
              <div
                key={i}
                className="rounded-2xl p-6 flex flex-col items-center text-center gap-3"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <span className="text-3xl">{p.icon}</span>
                <p className="font-bold text-sm" style={{ color: '#C4A882' }}>{p.title}</p>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(248,247,244,0.55)' }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto space-y-10">
          <div className="text-center space-y-3">
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#7C9E87' }}>
              Histórias reais
            </p>
            <h2
              className="font-serif"
              style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', color: '#F8F7F4' }}
            >
              Pessoas que estavam onde você está
            </h2>
          </div>

          {/* Asymmetric bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`rounded-3xl p-6 space-y-4 flex flex-col justify-between ${i === 0 ? 'md:col-span-2' : ''}`}
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: `1px solid ${t.color}33`,
                  minHeight: i === 0 ? '200px' : '180px',
                }}
              >
                <p
                  className="text-sm leading-relaxed italic"
                  style={{ color: 'rgba(248,247,244,0.8)' }}
                >
                  "{t.quote}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#F8F7F4' }}>{t.name}</p>
                    <p className="text-xs" style={{ color: 'rgba(248,247,244,0.45)' }}>{t.city}</p>
                  </div>
                  <span
                    className="text-xs font-bold px-3 py-1 rounded-full"
                    style={{ backgroundColor: `${t.color}22`, color: t.color, border: `1px solid ${t.color}44` }}
                  >
                    {t.days}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES BENTO ── */}
      <section className="py-20 px-6" style={{ backgroundColor: '#111120' }}>
        <div className="max-w-4xl mx-auto space-y-10">
          <div className="text-center space-y-3">
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#C4A882' }}>
              O que você encontra aqui
            </p>
            <h2
              className="font-serif"
              style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', color: '#F8F7F4' }}
            >
              Ferramentas feitas para quem está na luta
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div
                key={i}
                className={`rounded-3xl p-6 space-y-3 ${f.span}`}
                style={{
                  backgroundColor: i % 2 === 0 ? 'rgba(124,158,135,0.08)' : 'rgba(196,168,130,0.08)',
                  border: `1px solid ${i % 2 === 0 ? 'rgba(124,158,135,0.2)' : 'rgba(196,168,130,0.2)'}`,
                }}
              >
                <span className="text-3xl">{f.icon}</span>
                <p className="font-bold text-base" style={{ color: '#F8F7F4' }}>{f.title}</p>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(248,247,244,0.6)' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(124,158,135,0.15) 0%, transparent 70%)',
          }}
        />
        <div className="relative z-10 max-w-2xl mx-auto text-center space-y-8">
          <Image
            src="/assets/images/logo_1dia_transparente-1777268210110.png"
            alt="1 Dia de Cada Vez"
            width={140}
            height={50}
            className="object-contain mx-auto"
            style={{ maxHeight: '50px' }}
          />
          <h2
            className="font-serif leading-snug"
            style={{ fontSize: 'clamp(1.8rem, 5vw, 3rem)', color: '#F8F7F4' }}
          >
            O primeiro passo é o mais difícil.
            <br />
            <span style={{ color: '#7C9E87' }}>Mas você não precisa dar sozinho.</span>
          </h2>
          <p className="text-base leading-relaxed" style={{ color: 'rgba(248,247,244,0.65)' }}>
            Crie sua conta gratuita agora. Em menos de 2 minutos você já tem acesso ao chat ao vivo, ao rastreador de progresso e à comunidade de apoio.
          </p>
          <Link
            href="/sign-up-login"
            className="group inline-flex items-center gap-3 px-10 py-5 rounded-2xl text-lg font-bold transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: '#7C9E87',
              color: '#fff',
              boxShadow: '0 0 60px rgba(124,158,135,0.5)',
            }}
          >
            Criar Minha Conta Gratuita
            <span className="group-hover:translate-x-1 transition-transform duration-200">→</span>
          </Link>
          <div>
            <Link
              href="/apoie-a-pagina"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:scale-105"
              style={{
                backgroundColor: 'rgba(196,168,130,0.15)',
                border: '1px solid rgba(196,168,130,0.35)',
                color: '#C4A882',
              }}
            >
              💛 Apoie a Página
            </Link>
          </div>
          <p className="text-xs" style={{ color: 'rgba(248,247,244,0.35)' }}>
            Sem cartão de crédito · Sem compromisso · Confidencial
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer
        className="py-10 px-6"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-5">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <Link href="/quem-somos" className="text-xs transition-opacity hover:opacity-70" style={{ color: 'rgba(248,247,244,0.45)' }}>
              Quem Somos
            </Link>
            <Link href="/seja-voluntario" className="text-xs transition-opacity hover:opacity-70" style={{ color: 'rgba(248,247,244,0.45)' }}>
              Contato
            </Link>
            <Link href="/termos-de-uso" className="text-xs transition-opacity hover:opacity-70" style={{ color: 'rgba(248,247,244,0.45)' }}>
              Termos de Uso
            </Link>
            <Link href="/politica-de-privacidade" className="text-xs transition-opacity hover:opacity-70" style={{ color: 'rgba(248,247,244,0.45)' }}>
              Política de Privacidade
            </Link>
            <Link href="/sign-up-login" className="text-xs transition-opacity hover:opacity-70" style={{ color: 'rgba(248,247,244,0.45)' }}>
              Entrar
            </Link>
          </div>
          <p className="text-xs text-center" style={{ color: 'rgba(248,247,244,0.25)' }}>
            © 2026 1 Dia de Cada Vez · Plataforma gratuita de apoio ao vício em apostas
          </p>
        </div>
      </footer>
    </div>
  );
}
